import { createClient } from '@supabase/supabase-js';
import { Database } from '@/types/database.types';

// Get environment variables
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

// Validate environment variables
const isValidConfig = supabaseUrl && supabaseAnonKey && 
  supabaseUrl.startsWith('http') && 
  supabaseAnonKey.length > 20;

// Track if we've already warned about configuration
let hasWarnedAboutConfig = false;

// Create a function to validate and create the client
function createSupabaseClient() {
  if (!isValidConfig) {
    if (!hasWarnedAboutConfig) {
      console.warn('Supabase configuration is missing or invalid. Authentication features will be disabled.');
      hasWarnedAboutConfig = true;
    }
    
    // Return a mock client that provides no-op implementations
    return {
      auth: {
        signInWithPassword: async () => {
          return { data: { user: null, session: null }, error: { message: 'Supabase is not configured' } };
        },
        signUp: async () => {
          return { data: { user: null, session: null }, error: { message: 'Supabase is not configured' } };
        },
        signOut: async () => {
          return { error: null };
        },
        getSession: async () => {
          return { data: { session: null }, error: null };
        },
        getUser: async () => {
          return { data: { user: null }, error: null };
        },
        onAuthStateChange: (callback: any) => {
          // Call callback immediately with null session
          setTimeout(() => callback('SIGNED_OUT', null), 0);
          return { 
            data: { 
              subscription: { 
                unsubscribe: () => {} 
              } 
            } 
          };
        },
        refreshSession: async () => {
          return { data: { session: null, user: null }, error: null };
        }
      },
      from: () => ({
        select: () => ({
          eq: () => ({
            single: async () => ({ data: null, error: { message: 'Supabase is not configured' } })
          })
        })
      }),
      rpc: async () => {
        return { data: null, error: { message: 'Supabase is not configured' } };
      }
    } as any;
  }

  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce'
    },
    global: {
      headers: {
        'X-Client-Info': 'privateers-society'
      }
    }
  });
}

// Create and export the Supabase client
export const supabase = createSupabaseClient();

// Export a helper to check if Supabase is configured
export const isSupabaseConfigured = isValidConfig;

export { createClient };